export class Utils {
    /**
     * Evaluates a provided number to see if its even.
     * @param num Provided number to be evaluated.
     * @returns a boolean asserting if the number was even or not.
     */
    static IsEven(num) {
        return num % 2 == 0;
    }
    /**
     * Evaluates a provided number to see if it's odd.
     * @param num Provided number to be evaluated.
     * @returns a boolean asserting if the number was odd or not.
     */
    static IsOdd(num) {
        return !Utils.IsEven(num);
    }
    /**
     * Taking in a string, removes trailing whitespaces and extra spaces between words.
     * @param str String to be formatted.
     * @returns Formatted string without trailing whitespaces and extra spaces between words.
     */
    static trimAndReduce(str) {
        return str.trim().replace("\s+", " ");
    }
    /**
     * Taking the root styles of the given page and the provided rem value returns the rem value as pixels.
     * @param rem String or Number rem value to be parsed and converted into pixels.
     * @returns A pixel interpretation of the provided rem value.
     */
    static convertRemToPixels(rem) {
        const pxToRem = parseFloat(getComputedStyle(document.documentElement).fontSize);
        let numberRem = 0;
        if (typeof rem !== "number") {
            rem = Utils.trimAndReduce(rem);
            if (rem.includes("rem")) {
                rem = rem.replace("rem", "");
            }
            numberRem = parseFloat(rem);
        }
        else {
            numberRem = rem;
        }
        return numberRem * pxToRem;
    }
    /**
     * Taking in an element evaluates if the element is visible within the screens viewport.
     * @param element Element to be evaluated for visibility within the screens viewport.
     * @param horizontalOnly Optional Parameter that checks if the element is visible only caring about the horizontal axis.
     * @param verticalOnly Optional Parameter that checks if the element is visible only caring about the vertical axis.
     * @returns Boolean representation if the element is visible within the viewport.
     */
    static IsVisibleInViewport(element, horizontalOnly = false, verticalOnly = false) {
        const rect = element.getBoundingClientRect();
        if (horizontalOnly) {
            return (rect.left >= 0 &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth));
        }
        if (verticalOnly) {
            return (rect.top >= 0 &&
                rect.bottom <= ((window.innerHeight + rect.height) || document.documentElement.clientHeight));
        }
        return (rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= ((window.innerHeight + rect.height) || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth));
    }
    ;
    /**
    * Taking in an element evaluates if the element is visible within the given container.
    * @param element Element to be evaluated for visibility within the container.
    * @param container Container of element to evaluate against the element to check the elements visibility.
    * @param horizontalOnly Optional Parameter that checks if the element is visible only caring about the horizontal axis.
    * @param verticalOnly Optional Parameter that checks if the element is visible only caring about the vertical axis.
    * @returns Boolean representation if the element is visible within the container.
    */
    static IsVisibleInContainer(element, container, horizontalOnly = false, verticalOnly = false) {
        const rect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        if (horizontalOnly) {
            return (rect.left >= containerRect.left &&
                rect.right <= containerRect.right);
        }
        if (verticalOnly) {
            return (rect.top >= containerRect.top &&
                rect.bottom <= containerRect.bottom);
        }
        return (rect.top >= containerRect.top &&
            rect.left >= containerRect.left &&
            rect.bottom <= containerRect.bottom &&
            rect.right <= containerRect.right);
    }
    ;
    /**
     * A function to take the height of a reference element and assign the height to the target elements styles somehow.
     *
     * @param reference Used for getting the height of the desired element and using the height on the new element.
     * @param target The new element to recieve the height value from the reference element.
     * @param property The given style property the height should be assigned to
     */
    static copyHeight(reference, target, property = "height") {
        var referenceHeight = reference.clientHeight;
        target.style.setProperty((property), (referenceHeight + "px"));
    }
    ;
    /**
    * After scrolling a fixed distance display/ update the given element to make a seamless scrolling experience.
    * If no values are provided distance will be set to 0.
    *
    * @param targetElement The element to have the styles/ classes applied to.
    * @param rootElement The root node of the document to retrieve variables from.
    * @param heightStyleProperty Defaulting to "--nav-height" this variable is for selecting the correct style
    * on the root element for deciding the scroll distance.
    * @param classList Array of strings representing classes to be added and removed on scroll, strings starting with "!" are
    * removed when scroll distanceScrolled > distance given and added when distanceScrolled < distance
    * @param distanceOverride Optional override value to use when the root element distance isn't what you want.
    * @param distanceScrolled Optional parameter taking in the distance scrolled to avoid computing it twice if already calculated.
    * @returns A boolean value representing on when the Element should have all the classes apllied.
    */
    static updateClassesOnScroll(targetElement, rootElement, heightStyleProperty = "--nav-height", classList = [], distanceOverride, distanceScrolled) {
        var distance = 0;
        var updatedElement = false;
        if (classList.length > 0) {
            if (rootElement != null) {
                var variableHeight = rootElement.style.getPropertyValue(heightStyleProperty);
                if (variableHeight != '') {
                    distance = parseInt(variableHeight, 10);
                }
            }
            if (distance == 0 && distanceOverride != null) {
                distance = distanceOverride;
            }
            if (distanceScrolled == null) {
                distanceScrolled = (document.documentElement || document.body.parentNode || document.body).scrollTop;
            }
            if (distance <= distanceScrolled) {
                updatedElement = true;
                for (var i in classList) {
                    var className = classList[i];
                    if (className.startsWith("!")) {
                        targetElement.classList.remove(className.slice(1));
                    }
                    else {
                        targetElement.classList.add(className);
                    }
                }
            }
            else {
                updatedElement = false;
                for (var i in classList) {
                    var className = classList[i];
                    if (className.startsWith("!")) {
                        targetElement.classList.add(className.slice(1));
                    }
                    else {
                        targetElement.classList.remove(className);
                    }
                }
            }
        }
        return updatedElement;
    }
    ;
    /**
     * Create an IntersectionOberserverCallback to run on intersection success updating affected element with "scroll-visible" class.
     * @returns IntersectionObserverCallback to passed into and run from an IntersectionObserver.
     */
    static FadeIntoViewportCallback() {
        const callback = (entries) => {
            // Sort entries in order of when they triggered
            entries.sort(x => x.time);
            entries.forEach(entry => {
                // If the entry is intersecting when the callback was triggered
                if (entry.isIntersecting) {
                    const element = entry.target;
                    // Update the classList of the affected Element
                    element.classList.add("scroll-visible");
                }
            });
        };
        return callback;
    }
    /**
     * Creates and attaches IntersectionObservers to a given target element running required callback functionality on Intersection with optional container or viewport.
     * @param target Target element for the Intersection Observer to be attached to.
     * @param container Optional scrollable container for the Intersection Observer to run within, if not provided uses the screens viewport.
     * @param rootMargin How much area of the given container/ viewport should be ignored when seeing if an element is within view.
     * @param threshold How much of the affected element needs to be visible within the container/ viewport to trigger the Observer Intersected event.
     * @param callback An IntersectionObserverCallback to be run on Intersection with the target and container.
     */
    static RespondToVisibility(target, container, rootMargin = "0px 0px 0px 0px", threshold = 1.0, callback) {
        // Define the options for the Intersection Observer to use
        let options = {
            rootMargin: rootMargin,
            threshold: threshold
        };
        if (container != null) {
            options.root = container;
        }
        // Create the observer instace to be attached to the target
        const observer = new IntersectionObserver(callback, options);
        // Attach observer to target Element
        observer.observe(target);
    }
    /**
     * Initializes the JQueryValidation plugin on a given form selector with the provided validation rules and messages.
     * @param formSelector The CSS Selector of the form to be validated.
     * @param validationRules The validation rules to be applied to the form fields.
     * @param validationMessages The validation messages to be displayed for each field when validation fails.
     */
    static InitializeFormValidation(formSelector, validationRules, validationMessages = undefined) {
        $(formSelector).validate({
            errorClass: "is-invalid",
            ignore: ":hidden",
            submitHandler: function (form) {
                $.ajax({
                    type: "POST",
                    url: form.action,
                    data: $(form).serialize(), // serializes the form's elements.
                    success: function (data) {
                        const redirectUrl = $(form).find("#redirectUrl").val();
                        window.location.assign(redirectUrl);
                    },
                    error: (jqxXHR, textStatus, errorThrown) => { Utils.HandleJQueryValidationError(jqxXHR, form); }
                });
            },
            invalidHandler: function (event, validator) {
                // 'this' refers to the form
                var errors = validator.numberOfInvalids();
            },
            errorPlacement: (error, element) => {
                var _a;
                const elementName = (_a = element.attr("name")) === null || _a === void 0 ? void 0 : _a.toLowerCase();
                error.insertAfter(`.form-${elementName}-flag`);
            },
            rules: validationRules,
            messages: validationMessages
        });
    }
    /**
     * A callback function to be used with JQueryValidation's form submit within the error parameter to handle
     * submit errors and display the errors into the calling form.
     * @param jqxXHR Jquery XHR response from the submit request for processing.
     * @param textStatus The Error Text status returned by the failed request.
     * @param errorThrown The error thrown as a string from the failed request.
     * @param form The calling form element used for making the request for populating with errors.
     */
    static HandleJQueryValidationError(jqxXHR, form, textStatus = null, errorThrown = null) {
        let responseJSON = jqxXHR.responseJSON;
        // If responseJSON contains a field called errors update responseJSON to be the errors field to keep using following code
        if (responseJSON.errors !== undefined) {
            responseJSON = responseJSON.errors;
        }
        for (let i in responseJSON) {
            // Get each field from the responseJSON array
            let field = responseJSON[i];
            // Create a boolean flag for displaying if the field contains an error field as we will need to parse the error message
            // field not the field itself
            let modelError = false;
            // If the field contains an errors field update the field to be the errors field and update the modelError flag
            if (field.errors !== undefined) {
                field = field.errors;
                modelError = true;
            }
            // If the field has content
            if (field.length > 0) {
                // Target the form fields error span
                let errorField = $(form).find(`.form-${i.toLowerCase()}-flag`);
                // Get the error text to populate the errorField span either from the field itself or the nested errorMessage field
                let errorText = modelError ? field[0].errorMessage : field[0];
                // If there is more than one error message field in the model append the extra messages to the error text
                if (field.length > 1) {
                    for (let i = 1; i < field.length; i++) {
                        errorText += `${modelError ? field[i].errorMessage : field[i]}<br/>`;
                    }
                }
                // Update the error field span with the error text
                errorField.text(errorText);
                errorField.show();
            }
        }
    }
}
